<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import type { Member } from '../../lib/api/types';
  import { currentUser, isAuthenticated } from '../../lib/stores/auth';
  import type { PlaceCategory } from '../../lib/api/types';
  import DayTab from './DayTab.svelte';
  import Insights from './Insights.svelte';
  import Places from './Places.svelte';
  import Cities from './Cities.svelte';
  import Trips from './Trips.svelte';
  import Imports from './Imports.svelte';
  import Explorer from './Explorer.svelte';
  import Recap from './Recap.svelte';
  import YearTab from './YearTab.svelte';
  import Regions from './Regions.svelte';
  import PlacePage from './PlacePage.svelte';
  import Search from './Search.svelte';
  import Commutes from './Commutes.svelte';
  import { t } from '../../lib/i18n/index.ts';

  // Timeline is a member's timeline in tabs (spec 005): the caller's own
  // at /timeline/me, or a group member's at /timeline/:groupId/:userId. The
  // caller's own adds the explorer's views, search, and notes (spec 005),
  // which no group sees.
  let { groupId, userId }: { groupId?: string; userId?: string } = $props();

  const tabs = [
    { id: 'day', label: t('timelineTabDay'), icon: 'la-calendar-day' },
    { id: 'trips', label: t('timelineTabTrips'), icon: 'la-suitcase' },
    { id: 'insights', label: t('timelineTabInsights'), icon: 'la-chart-bar' },
    { id: 'recap', label: t('timelineTabRecap'), icon: 'la-trophy', own: true },
    { id: 'year', label: t('timelineTabYear'), icon: 'la-calendar', own: true },
    { id: 'explorer', label: t('timelineTabExplorer'), icon: 'la-map', own: true },
    { id: 'places', label: t('timelineTabPlaces'), icon: 'la-map-marker' },
    { id: 'cities', label: t('timelineTabCities'), icon: 'la-globe-americas' },
    { id: 'regions', label: t('timelineTabRegions'), icon: 'la-flag', own: true },
    { id: 'commutes', label: t('timelineTabCommutes'), icon: 'la-exchange-alt', own: true },
    { id: 'imports', label: t('timelineTabImports'), icon: 'la-file-import', own: true },
  ];
  let tab = $state('day');
  let category = $state<PlaceCategory | ''>('');
  // placesFrom is the period Insights had open when a category was picked.
  let placesFrom = $state('');
  // dayDate is the day a trip opens in the Day tab, and dayEntry the stay
  // or trip picked in it; dayOpened remounts the Day tab for each.
  let dayDate = $state<string | undefined>(undefined);
  let dayEntry = $state<string | undefined>(undefined);
  let dayOpened = $state(0);
  // place is the place whose page the Places tab shows.
  let place = $state<{ id: string; name?: string; address?: string; category?: PlaceCategory; city?: string } | null>(null);
  // tripStart is the trip away the Trips tab opens on.
  let tripStart = $state<string | undefined>(undefined);
  let searchText = $state('');
  let query = $state('');
  let members: Member[] = $state([]);

  const subject = $derived(userId ?? $currentUser?.id ?? '');
  const self = $derived(!groupId || subject === $currentUser?.id);
  const member = $derived(members.find((m) => m.user_id === subject));

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  $effect(() => {
    if (groupId) api.get<Member[]>(`/groups/${groupId}/members`).then((list) => (members = list)).catch(() => {});
  });

  function pickMember(id: string) {
    navigate(`/timeline/${groupId}/${id}`);
  }

  function pickTab(id: string) {
    tab = id;
    place = null;
    dayEntry = undefined;
    tripStart = undefined;
  }

  function openDay(date: string, entry?: string) {
    dayDate = date;
    dayEntry = entry;
    dayOpened++;
    tab = 'day';
  }

  function openPlace(p: { id?: string; place_id?: string; name?: string; address?: string; category?: PlaceCategory; city?: string }) {
    const id = p.id ?? p.place_id;
    if (!id) return;
    place = { id, name: p.name, address: p.address, category: p.category, city: p.city };
    tab = 'places';
  }

  function search(e: SubmitEvent) {
    e.preventDefault();
    const q = searchText.trim();
    if (!q) return;
    query = q;
    tab = 'search';
  }
</script>

<div class="flex flex-col flex-1 min-h-0">
  <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 border-b" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <i aria-hidden="true" class="las la-stream text-xl"></i>
    {#if groupId}
      <select class="input w-56" value={subject} onchange={(e) => pickMember((e.target as HTMLSelectElement).value)} aria-label={t('timelineMember')}>
        {#if !member}<option value={subject}>{t('timelineTitle')}</option>{/if}
        {#each members as m (m.user_id)}
          <option value={m.user_id}>{m.display_name}</option>
        {/each}
      </select>
    {:else}
      <h1 class="font-semibold">{t('timelineMine')}</h1>
    {/if}
    <div class="flex gap-1 overflow-x-auto max-w-full order-last lg:order-none" role="tablist">
      {#each tabs.filter((x) => !x.own || self) as x (x.id)}
        <button role="tab" aria-selected={tab === x.id} class="btn btn-sm shrink-0 {tab === x.id ? 'btn-primary' : 'btn-secondary'}" onclick={() => pickTab(x.id)}>
          <i aria-hidden="true" class="las {x.icon} mr-1"></i>{x.label}
        </button>
      {/each}
    </div>
    {#if self}
      <form class="ml-auto flex items-center gap-1" role="search" onsubmit={search}>
        <input type="search" class="input w-48 sm:w-56" placeholder={t('timelineSearchMine')} maxlength="200" bind:value={searchText} aria-label={t('timelineSearchMine')} />
        <button type="submit" class="btn btn-secondary btn-sm" aria-label={t('timelineSearch')}><i aria-hidden="true" class="las la-search"></i></button>
      </form>
    {/if}
  </div>
  {#if subject}
    {#key subject}
      {#if tab === 'day'}
        {#key dayOpened}
          <DayTab {groupId} userId={subject} {self} initialDate={dayDate} initialEntry={dayEntry} />
        {/key}
      {:else if tab === 'trips'}
        <Trips {groupId} userId={subject} {self} initialStart={tripStart} onDay={(d) => openDay(d)} />
      {:else if tab === 'insights'}
        <Insights {groupId} userId={subject} {self} onCategory={(c, p) => { category = c; placesFrom = p; tab = 'places'; }} />
      {:else if tab === 'places'}
        {#key placesFrom}
          {#if place && self}
            <PlacePage {place} onBack={() => (place = null)} onDay={(d) => openDay(d)} />
          {:else}
            <Places {groupId} userId={subject} {self} bind:category start={placesFrom} onPlace={self ? openPlace : undefined} />
          {/if}
        {/key}
      {:else if tab === 'cities'}
        <Cities {groupId} userId={subject} {self} />
      {:else if tab === 'recap' && self}
        <Recap onDay={openDay} onPlace={openPlace} onTrip={(s) => { tripStart = s; tab = 'trips'; }} />
      {:else if tab === 'year' && self}
        <YearTab onDay={(d) => openDay(d)} />
      {:else if tab === 'explorer' && self}
        <Explorer />
      {:else if tab === 'regions' && self}
        <Regions />
      {:else if tab === 'commutes' && self}
        <Commutes onDay={(d) => openDay(d)} />
      {:else if tab === 'search' && self}
        {#key query}
          <Search {query} onDay={openDay} />
        {/key}
      {:else if tab === 'imports' && self}
        <Imports />
      {/if}
    {/key}
  {/if}
</div>
